import React, { useEffect, useMemo, useState } from "react";
import { Modal, Box, Flex, Text, Loader } from "@mantine/core";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import useDrawerStyles from "./style";
import DropdownSelect from "./components/DropdownSelect";
import { getEligibleSigners } from "../../services/quarterlyReport";
import { decrypt } from "../../utils";

// A signer has a signature on file when either a stored image (drawn/uploaded,
// AES-encrypted URL) or a typed text signature exists.
const hasSignatureOnFile = (s) => !!(s?.signatureImage || s?.signature);

const SignatureModal = ({ opened, onClose, onConfirm, requireTitle, saving }) => {
  const { classes, cx } = useDrawerStyles();
  const { signers, signersLoading } = useSelector((state) => state.quarterlyReport);

  const [signerId, setSignerId] = useState(null);
  const [title, setTitle] = useState("");
  const [typedSignature, setTypedSignature] = useState("");
  const [touched, setTouched] = useState(false);
  const titleMissing = requireTitle && touched && !title.trim();

  const signer = (signers || []).find((s) => s.id === signerId);
  // Stored image URLs arrive AES-encrypted from the API, same as
  // Setting/MySignature — decrypt before handing the value to an <img>, but
  // tolerate an already-plain URL so a future unencrypted field still renders.
  const storedImage = useMemo(() => {
    const raw = signer?.signatureImage;
    if (!raw) return "";
    if (/^(https?:\/\/|data:image\/)/i.test(raw)) return raw;
    const url = decrypt(raw);
    return typeof url === "string" ? url : "";
  }, [signer?.signatureImage]);
  const storedText = signer?.signature || "";
  const hasStoredSignature = !!(storedImage || storedText);

  useEffect(() => {
    if (opened) {
      getEligibleSigners();
      setSignerId(null);
      setTitle("");
      setTypedSignature("");
      setTouched(false);
    }
  }, [opened]);

  useEffect(() => {
    if (!signerId && signers?.length) {
      setSignerId(signers[0].id);
      setTitle(signers[0].title || "");
    }
  }, [signers, signerId]);

  const handleSelectSigner = (opt) => {
    const s = (signers || []).find((m) => m.id === opt.value);
    setSignerId(s?.id);
    setTitle(s?.title || "");
    setTypedSignature("");
  };

  const confirm = () => {
    if (!signer) return;
    const missingSignature = !hasStoredSignature && !typedSignature.trim();
    const missingTitle = requireTitle && !title.trim();
    if (missingSignature || missingTitle) {
      setTouched(true);
      return;
    }
    const name = signer.label.replace(/\s*\(you\)\s*/i, "");
    // Prefer the signature on file (image URL, then typed text) and only fall
    // back to what was typed here when the signer has nothing stored.
    const signatureText = storedImage || storedText || typedSignature.trim();
    onConfirm({
      signerId: signer.id,
      nameLine: title ? `${name}, ${title}` : name,
      title,
      signature: signatureText,
      date: dayjs().format("MMM D, YYYY"),
    });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add your signature" radius="md" size="md" zIndex={1100}>
      {signersLoading && !signers?.length ? (
        <Flex justify="center" py={30}>
          <Loader size="sm" color="violet" />
        </Flex>
      ) : (
        <>
          <Box className={classes.modalBody}>
            <Box>
              <Text className={classes.wizLabel} mb={6}>
                Completed by
              </Text>
              <DropdownSelect
                value={signerId}
                options={(signers || []).map((s) => ({ value: s.id, label: s.label }))}
                getMeta={(opt) => {
                  const s = (signers || []).find((m) => m.id === opt.value);
                  return hasSignatureOnFile(s) ? s.title : `${s?.title} · no signature on file`;
                }}
                onSelect={handleSelectSigner}
                placeholder={(signers || []).length ? "Select signer" : "No eligible signers found"}
              />
            </Box>

            <Box>
              <Text className={classes.wizLabel} mb={6}>
                Title {requireTitle && <span className={classes.requiredMark}>*</span>}
              </Text>
              <input
                className={cx(classes.input, titleMissing && classes.inputError)}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              {titleMissing && (
                <Text color="red" size="xs" mt={4}>
                  A reviewer title is required before this report can be finalized.
                </Text>
              )}
            </Box>

            <Box>
              <Text className={classes.wizLabel} mb={6}>
                Signature
              </Text>
              {storedImage ? (
                <img src={storedImage} alt="signature" className={classes.sigPreviewImage} />
              ) : storedText ? (
                <Text className={classes.sigPreviewScript}>{storedText}</Text>
              ) : (
                <input
                  className={cx(classes.input, classes.inputItalic)}
                  placeholder="Type your full legal name to sign"
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                />
              )}
              {touched && !hasStoredSignature && !typedSignature.trim() && (
                <Text color="red" size="xs" mt={4}>
                  Type your name to sign.
                </Text>
              )}
            </Box>
          </Box>

          <Flex justify="flex-end" gap={10} mt={22} pt={16} className={classes.modalFoot}>
            <button className={classes.btnGhost} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className={classes.btnAdd} disabled={!signer || saving} onClick={confirm}>
              <Flex className={classes.btnInner}>
                {saving && <Loader size={13} color="currentColor" />}
                {saving ? "Signing…" : "Sign Document"}
              </Flex>
            </button>
          </Flex>
        </>
      )}
    </Modal>
  );
};

export default SignatureModal;
